"use client"
import React, { useState, useEffect } from 'react';
import Button from '@aura-design/system/button';

type GalleryTypes = {
  children: React.ReactNode[];
};

const Gallery = ({ children }: GalleryTypes) => {
  const [step, setStep] = useState(0);
  const [dragStartX, setDragStartX] = useState<number | null>(null);
  const [isHovered, setIsHovered] = useState(false);

  const paginatorArray = Array.from({ length: children.length }, (_, i) => i);

  const handleStart = (e: React.MouseEvent | React.TouchEvent) => {
    setDragStartX(getClientX(e));
  };

  const handleMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (dragStartX !== null && !isHovered) {
      const currentX = 'touches' in e ? e.touches[0].pageX : e.pageX;
      const deltaX = currentX - dragStartX;

      // Adjust step based on the direction of the drag
      if (deltaX > 50) {
        // Dragged to the right, increase step
        setStep((prevStep) => Math.max(prevStep - 1, 0));
        setDragStartX(null);
      } else if (deltaX < -50) {
        // Dragged to the left, decrease step
        setStep((prevStep) => Math.min(prevStep + 1, children.length - 1));
        setDragStartX(null);
      }
    }
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const getClientX = (e: React.MouseEvent | React.TouchEvent) => {
    return 'touches' in e ? e.touches[0].clientX : e.clientX;
  };

  useEffect(() => {
    // Set up an interval to automatically change the step every 3 seconds
    const intervalId = setInterval(() => {
      if (!isHovered) {
        setStep((prevStep) => (prevStep + 1) % children.length);
      }
    }, 14000);

    // Clear the interval when the component unmounts
    return () => clearInterval(intervalId);
  }, [children.length, isHovered]);

  return (
    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleStart}
      onMouseMove={handleMove}
      onTouchStart={handleStart}
      onTouchMove={handleMove}
      style={{ overflow: 'hidden' }}
    >
      <div style={{ overflow: 'hidden' }}>
        {children.map((slide, index) => {
          const isActive = index === step;

          return (
            <div
              key={index}
              style={{
                display: isActive ? 'block' : 'none',
                transition: 'transform 0.3s ease-in-out',
                width: '100%',
                userSelect: 'none',
              }}
              className="fade-in"
            >
              {slide}
            </div>
          );
        })}
      </div>
      {paginatorArray.length > 1 ? (
        <div className="absolute bottom-0 left-0 right-0 z-30">
          <div className="m-auto max-w-20">
            <ul className="nav-list">
              {paginatorArray.map((item) => {
                const isActive = item === step;
                return (
                  <li key={item}>
                    <Button mode="menu" onClick={() => setStep(item)}>
                      <div
                        className={`${
                          isActive ? 'bg-primary' : 'bg-accents-1'
                        } circle h-0.5 w-0.5`}
                      ></div>
                    </Button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Gallery;
